import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useClerk } from '@clerk/clerk-react';
import { DevAuthProvider, useDevAuth } from './DevAuthProvider';
import { ClerkAuthProvider, useClerkCurrentUser } from './ClerkAuthProvider';
import { LccUser } from './types';
import { getSettingsRepository } from '../services/settings/repositoryFactory';
import { CurrentUserProfile } from '../services/settings/types';
import { useApplicationContext } from './ApplicationContext';

const hasClerkKey = Boolean(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY);
const settingsRepo = getSettingsRepository();

/**
 * Punto unico de entrada de autenticacion para toda la plataforma.
 * Si existe VITE_CLERK_PUBLISHABLE_KEY -> Clerk real.
 * Si no -> selector de usuario de desarrollo (misma interfaz useCurrentUser()).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  if (hasClerkKey) {
    return <ClerkAuthProvider>{children}</ClerkAuthProvider>;
  }
  return <DevAuthProvider>{children}</DevAuthProvider>;
}

/**
 * Identidad basica del proveedor de autenticacion activo (Clerk o Dev). Ya NO
 * define el rol/pantallas definitivos: eso se resuelve en el backend contra
 * Esq_Configuration.TB_Configuration_User (ver CurrentUserProvider mas abajo).
 */
function useIdentity(): LccUser {
  if (hasClerkKey) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useClerkCurrentUser();
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useDevAuth().user;
}

/**
 * Identidad basica (Clerk o Dev) SIN pasar por CurrentUserProvider - util para
 * pantallas que viven FUERA de una aplicacion (ej. el Selector de Aplicacion),
 * donde todavia no hay una aplicacion activa contra la cual resolver
 * Rol/Pantallas. Solo trae id/nombre; nunca usar el `role` de aqui para
 * decidir permisos (siempre desactualizado/generico - ver useCurrentUser).
 */
export function useAuthIdentity(): LccUser {
  return useIdentity();
}

export { useDevAuth };
export type { LccUser };

// ---------------------------------------------------------------------
// Perfil autoritativo (id interno, Rol, pantallas permitidas), resuelto
// por el backend. Se consulta UNA vez por sesion y se comparte via
// contexto para toda la app (barra de navegacion, guards de rutas, etc).
// ---------------------------------------------------------------------
interface CurrentUserContextValue {
  user: LccUser;
  allowedModules: string[] | null;
  refreshProfile: () => void;
}

const CurrentUserContext = createContext<CurrentUserContextValue | null>(null);

export function CurrentUserProvider({ children }: { children: ReactNode }) {
  const identity = useIdentity();
  const { applicationCode } = useApplicationContext();
  const [profile, setProfile] = useState<CurrentUserProfile | null>(null);

  const load = useCallback(() => {
    if (!applicationCode) return;
    settingsRepo.getMe().then(setProfile);
  }, [applicationCode]);

  useEffect(() => {
    setProfile(null); // evita mostrar por un instante el Rol de la aplicacion anterior al cambiar de app
    load();
  }, [identity.id, applicationCode, load]);

  if (!applicationCode || !profile) {
    return <div className="flex h-screen items-center justify-center text-ink-400">Cargando perfil…</div>;
  }

  const user: LccUser = { id: profile.id, fullName: profile.fullName, role: profile.role };

  return (
    <CurrentUserContext.Provider value={{ user, allowedModules: profile.allowedModules, refreshProfile: load }}>
      {children}
    </CurrentUserContext.Provider>
  );
}

/** Usuario autenticado con su Rol y su id INTERNO (UUID de TB_Configuration_User), ya resuelto por el backend. */
export function useCurrentUser(): LccUser {
  const ctx = useContext(CurrentUserContext);
  if (!ctx) throw new Error('useCurrentUser debe usarse dentro de <CurrentUserProvider>');
  return ctx.user;
}

/** Pantallas permitidas del usuario (override sobre el default de su rol). null = usa el default del rol. */
export function useAllowedModules(): string[] | null {
  const ctx = useContext(CurrentUserContext);
  if (!ctx) throw new Error('useAllowedModules debe usarse dentro de <CurrentUserProvider>');
  return ctx.allowedModules;
}

/**
 * Cierra la sesion del usuario. Devuelve `null` en modo desarrollo (sin Clerk
 * configurado), ya que ahi no existe una sesion real que cerrar - en ese caso
 * la pantalla de Configuracion debe ocultar el boton de "Cerrar sesión".
 */
export function useSignOut(): (() => void) | null {
  if (hasClerkKey) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { signOut } = useClerk();
    return () => signOut();
  }
  return null;
}
